import { useMemo, useState } from "react";
import { PDFService } from "../services/pdf-service";
import { useToast } from "../context/toast-context";
import { Button, CircularProgress, Tooltip } from "@mui/material";
import { useTranslation } from "react-i18next";

interface DownloadPDFProps {
  userId: number;
  employeeName: string;
  variant?: "default" | "mui";
  disabled?: boolean;
  branchId?: number;
}

export function DownloadPDF({
  userId,
  employeeName,
  variant = "default",
  disabled = false,
  branchId,
}: DownloadPDFProps) {
  const [loading, setLoading] = useState(false);

  const pdfService = useMemo(() => new PDFService(), []);
  const { showToast } = useToast();
  const { t } = useTranslation();

  const handleDownloadPDF = async () => {
    setLoading(true);
    await pdfService.getPDF(
      { userId, ...(branchId ? { branchId } : {}) },
      (data) => {
        const buffer = new Uint8Array(data.pdf.data);

        const blob = new Blob([buffer], { type: "application/pdf" });

        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = blobUrl;

        const filename = employeeName.split(" ").join("_");
        link.download = `${filename}.pdf`;
        document.body.appendChild(link);
        link.click();

        // Liberamos la memoria del objectURL y limpiamos el DOM
        link.remove();
        URL.revokeObjectURL(blobUrl);
      },
      (error) => showToast(error, "error")
    );
    setLoading(false);
  };

  if (variant === "default") {
    return (
      <button
        disabled={loading}
        onClick={handleDownloadPDF}
        className="px-24 hover:bg-black/90 mx-auto py-1 flex items-center text-white bg-black rounded-xl disabled:opacity-50"
      >
        {loading ? (
          <CircularProgress
            sx={{
              color: "#fff",
            }}
            size="1.5rem"
            className="animate-spin "
          />
        ) : (
          t("account-verified.employee.download")
        )}
      </button>
    );
  }
  return (
    <Tooltip
      title={disabled ? t("employee.info.download-disabled") : ""}
      open={disabled ? undefined : false}
    >
      <span>
        <Button
          onClick={handleDownloadPDF}
          disabled={loading || disabled}
          sx={{
            padding: "6px 16px",
            minWidth: "auto",
            background: "transparent",
            border: `1px solid ${disabled ? "#9CA3AF" : "#EF0BB8"}`,
            borderRadius: "20px",
            color: disabled ? "#9CA3AF" : "#EF0BB8",
            textTransform: "none",
            fontFamily: "Montserrat",
            fontWeight: 500,
            fontSize: "0.875rem",
            '&:hover': {
              borderColor: disabled ? "#9CA3AF" : "#EF0BB8",
              backgroundColor: disabled ? "transparent" : "rgba(239, 11, 184, 0.04)",
              boxShadow: disabled ? "none" : "0 2px 4px rgba(239, 11, 184, 0.1)",
            },
            '&:active': {
              backgroundColor: disabled ? "transparent" : "rgba(239, 11, 184, 0.08)",
              boxShadow: "none",
            },
          }}
          startIcon={
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M19 9H15V3H9V9H5L12 16L19 9ZM5 18V20H19V18H5Z" fill={disabled ? "#9CA3AF" : "#EF0BB8"}/>
            </svg>
          }
        >
          {loading ? (
            <CircularProgress
              sx={{
                color: "#EF0BB8",
              }}
              size="1.5rem"
              className="animate-spin"
            />
          ) : (
            t("employee.info.download")
          )}
        </Button>
      </span>
    </Tooltip>
  );
}
